import React, {
  useId,
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { FiChevronRight, FiInfo, FiLayers } from "react-icons/fi";
import { Button } from "../../../components/Button";
import { Tooltip } from "../../../components/Tooltip";
import { ImageGalleryModal } from "../../../components/ImageGalleryModal";
import { useTranslation } from "react-i18next";
import { Badge } from "../../../components/Badge";
import { Amenity } from "../../../api/contracts/category-ads";
import { Image } from "../../../api/contracts/images";
import { motion, AnimatePresence } from "framer-motion";
import { formatPriceWithLocale } from "../../../utils/numbers";
import { shouldShowDiscount } from "../../../utils/shouldShowDiscount";
import { getNaturalSortComparator } from "../../../utils/sort";
import { Clock } from "lucide-react";
import { InfoHint } from "../../../components/InfoHint";
import { StableImage } from "../../../components/StableImage";
import { usePreloadedImageSrc } from "../../../hooks/usePreloadedImageSrc";
import { useClampTruncation } from "../../../hooks/useClampTruncation";
import { AmenityChips } from "./AmenityChips";
import { ExtraBedsBadge } from "./ExtraBedsBadge";
import {
  BedsIncludedDescription,
  BedsIncludedIcon,
} from "./BedsIncludedIcon";

interface LargeCardProps {
  images: Image[];
  /** The applied code's room cap is shared across bookings, so the deal can run
   *  out mid-session. Renders an overlay badge on the image. */
  limitedAvailability?: boolean;
  title: string;
  price: number;
  priceFrom?: boolean;
  /** Nights the shown price covers; a stay of two or more says the price is the total. */
  nights?: number;
  /** Pre-discount price; shown struck-through before `price` when it differs. */
  originalPrice?: number;
  /** Area in m² (for non-plot categories), e.g. "25" or "25 - 30" */
  area?: string | null;
  /** Length range in meters (for plots), e.g. "10" or "10 - 15" */
  lengthRange?: string | null;
  /** Width range in meters (for plots), e.g. "8" or "8 - 12" */
  widthRange?: string | null;
  description: string;
  amenities: Amenity[];
  onSelect?: () => void;
  showPriceAndSelect?: boolean;
  compactUnitStyle?: boolean;
  onViewRoomInfo?: () => void;
  showAvailabilityButton?: boolean;
  isSelected?: boolean;
  /** Render the card as present but unselectable — a category that exists on the
   *  package but cannot be chosen for the current dates or guest mix. The card
   *  keeps its full content so the guest can still judge it; only the action is
   *  withdrawn. Defaults to false, so existing call sites are unaffected. */
  disabled?: boolean;
  /** Why the card is unselectable, badged over the card image. Ignored unless
   *  `disabled`. */
  disabledReason?: string | null;
  onImageError?: (url: string) => void;
  /** Opens the v2 booking flow's detail step for this category. Renders a
   *  secondary "Se mer" button to the left of the primary action when set;
   *  omitted, the card is unchanged. Ignored when `compactUnitStyle` is set —
   *  the unit accordion's card has no such button. */
  onViewDetail?: () => void;
  /** Extra beds and cot choice, shown under the price. */
  bedsSlot?: React.ReactNode;
  /** The booking brings beds: Välj carries an info icon and a description. */
  includesBeds?: boolean;
  /** Extra beds the booking comes with; a badge over the photo says so. */
  extraBedsCount?: number;
  extraBedPricePerNight?: number | null;
}

/**
 * Card price line. Keeps the amount and currency on one line: a long amount
 * truncates with an ellipsis while the currency stays fully visible. A
 * configured 0 SEK stay shows as "0 SEK"; only non-finite values use the
 * unavailable placeholder (cards without a resolved price hide this line).
 */
const CardPrice: React.FC<{
  price: number;
  originalPrice?: number;
  priceFrom?: boolean;
  nights?: number;
}> = ({ price, originalPrice, priceFrom = false, nights = 0 }) => {
  const { t } = useTranslation();

  if (!Number.isFinite(price)) {
    return (
      <p className="truncate text-sm font-medium leading-[1.21] text-accent">
        {t("landing.large_card.price_unavailable")}
      </p>
    );
  }

  return (
    <p className="flex items-baseline gap-1 text-sm font-medium leading-[1.21] text-accent">
      {shouldShowDiscount(price, originalPrice) && (
        <span
          data-testid="original-price"
          className="shrink-0 line-through opacity-60"
        >
          {formatPriceWithLocale(originalPrice)} {t("booking.currency")}
        </span>
      )}
      {priceFrom && (
        <span className="shrink-0">{t("landing.large_card.from")}</span>
      )}
      <span className="min-w-0 truncate">{formatPriceWithLocale(price)}</span>
      <span className="shrink-0">{t("booking.currency")}</span>
      {nights >= 2 && (
        <span
          data-testid="card-price-total-nights"
          className="min-w-0 truncate text-xs font-normal text-ink-secondary"
        >
          {t("landing.large_card.total_for_nights", { count: nights })}
        </span>
      )}
    </p>
  );
};

/**
 * Overlay pill on a card image: the code's room cap is shared across bookings,
 * so the deal can be taken by someone else while this guest deliberates.
 *
 * `pointer-events-none` so it never swallows the image's own click (which opens
 * the gallery), and the amber fill keeps dark text legible over a photo.
 */
const LimitedAvailabilityBadge: React.FC<{ show?: boolean }> = ({ show }) => {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <Badge
      variant="default"
      data-testid="limited-availability-badge"
      className="pointer-events-none absolute right-2 top-2 z-10 shadow-sm"
      icon={<Clock className="h-3 w-3" />}
      text={t("booking.limited_offer")}
    />
  );
};

export const LargeCard: React.FC<LargeCardProps> = ({
  images,
  title,
  price,
  originalPrice,
  priceFrom = false,
  nights,
  area,
  lengthRange,
  widthRange,
  description,
  amenities,
  onSelect,
  showPriceAndSelect,
  compactUnitStyle = false,
  onViewRoomInfo,
  showAvailabilityButton = false,
  isSelected = false,
  disabled = false,
  disabledReason = null,
  onImageError,
  limitedAvailability,
  onViewDetail,
  bedsSlot,
  includesBeds = false,
  extraBedsCount = 0,
  extraBedPricePerNight,
}) => {
  const { t, i18n } = useTranslation();
  const bedsDescriptionId = useId();
  const bedsA11y = includesBeds
    ? ({ "aria-describedby": bedsDescriptionId } as const)
    : {};
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [direction, setDirection] = useState(0); // 🔑 track direction
  const hasRequestedImageRefresh = useRef(false);
  const { ref: titleRef, isTruncated: isTitleTruncated } =
    useClampTruncation<HTMLHeadingElement>(title);

  const amenityComparator = useMemo(
    () => getNaturalSortComparator(i18n.language),
    [i18n.language],
  );
  const sortedAmenities = useMemo(
    () => [...amenities].sort((a, b) => amenityComparator(a.name, b.name)),
    [amenities, amenityComparator],
  );
  const areaText =
    lengthRange && widthRange
      ? `${lengthRange} × ${widthRange} m`
      : area
        ? `${area} m²`
        : null;
  const currentImage = images[currentImageIndex];
  const firstImage = images[0];

  useEffect(() => {
    hasRequestedImageRefresh.current = false;
  }, [images]);

  const handleImageError = useCallback(
    (url: string) => {
      if (!onImageError || !url || hasRequestedImageRefresh.current) {
        return;
      }
      hasRequestedImageRefresh.current = true;
      onImageError(url);
    },
    [onImageError],
  );

  const displayedCurrentImageUrl = usePreloadedImageSrc({
    url: currentImage?.url,
    immediateKey: currentImageIndex,
    onError: handleImageError,
  });
  const displayedCurrentImage =
    images.find((image) => image.url === displayedCurrentImageUrl) ??
    currentImage;

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDirection(1);
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDirection(-1);
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleDotClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDirection(index > currentImageIndex ? 1 : -1);
    setCurrentImageIndex(index);
  };

  const handleImageClick = () => {
    setIsGalleryOpen(true);
  };

  // Action button — bottom-right on desktop, full-width at the card bottom on
  // mobile. Select when priced, otherwise the availability CTA.
  const renderActionButton = (placement: "inline" | "bottom") => {
    const isBottom = placement === "bottom";
    const visibility = isBottom
      ? "w-full shrink-0 lg:hidden"
      : "hidden shrink-0 whitespace-nowrap lg:inline-flex";

    const primary = showPriceAndSelect ? (
      <Button
        variant="default"
        size="medium"
        type="button"
        disabled={disabled || isSelected}
        className={visibility}
        onClick={disabled ? undefined : onSelect}
        onMouseEnter={
          disabled ? undefined : () => import("../../booking/Booking")
        }
        data-testid="select-button"
        {...bedsA11y}
      >
        {includesBeds && !isSelected && <BedsIncludedIcon />}
        {t(isSelected ? "landing.large_card.selected" : "landing.large_card.select")}
      </Button>
    ) : showAvailabilityButton && onViewRoomInfo ? (
      <Button
        variant="default"
        size={isBottom ? "medium" : "small"}
        type="button"
        className={
          isBottom
            ? visibility
            : `${visibility} !h-8 !rounded-full !px-3 !py-1 !text-xs !font-medium`
        }
        onClick={onViewRoomInfo}
      >
        {t("landing.large_card.view_availability")}
      </Button>
    ) : null;

    if (!onViewDetail) return primary;

    // Two `cta`-sized buttons don't fit on one row at this width — an
    // `inline-flex` button won't shrink below its own label, so squeezing them
    // into a shared row clips one instead of wrapping it. Stacked, full-width,
    // both fit without either the row or the label being cut.
    if (isBottom) {
      return (
        <div className={`flex flex-col gap-2 ${visibility}`}>
          <Button
            variant="secondary"
            size="medium"
            type="button"
            className="w-full"
            onClick={onViewDetail}
            data-testid="view-detail-button"
          >
            {t("booking_flow.cta.view_more")}
          </Button>
          {primary}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="small"
          type="button"
          className="!h-8 !rounded-full !px-3 !py-1 !text-xs !font-medium whitespace-nowrap"
          onClick={onViewDetail}
          data-testid="view-detail-button"
        >
          {t("booking_flow.cta.view_more")}
        </Button>
        {primary}
      </div>
    );
  };

  // Why the card can't be taken, badged over the photo: a guest scanning the
  // grid learns it from the image alone, before they reach the price. Capped
  // well short of the image so a long reason wraps into a badge rather than
  // banding across the whole photo. A card without a photo has nothing to
  // overlay, so the badge falls back into the title column.
  const renderDisabledBadge = (placement: "overlay" | "inline") => {
    if (!disabled || !disabledReason) return null;

    return (
      <>
        {/* Narrow screens get an icon instead of the sentence. Overlaid on a
            phone-width photo the pill ran out of room and clipped its own text
            mid-word, which reads as broken rather than as a reason. Tapping it
            says the same thing in a popover. */}
        {placement === "overlay" && (
          <div className="absolute right-3 top-3 z-10 rounded-full bg-surface-card/90 shadow-sm md:hidden">
            <InfoHint
              hint={disabledReason}
              label={disabledReason}
              data-testid="category-card-disabled-reason-icon"
            />
          </div>
        )}
        <div
          data-testid="category-card-disabled-reason"
          className={
            placement === "overlay"
              ? "pointer-events-none absolute right-3 top-3 z-10 hidden max-w-[66%] [overflow-wrap:anywhere] md:block"
              : "[overflow-wrap:anywhere]"
          }
        >
          <Badge
            variant="default"
            text={disabledReason}
            className="text-accent"
          />
        </div>
      </>
    );
  };

  return (
    <div
      className={
        compactUnitStyle
          ? // Carries the same shell as the compact package card it now sits
            // beside: without it a room read as loose content next to a card.
            `flex h-full w-full flex-col gap-3 overflow-hidden rounded-2xl border bg-surface-card p-3 xl:max-w-[201px] ${
              isSelected ? "border-primary ring-2 ring-primary" : "border-border"
            }`
          : `flex h-full flex-col gap-4 overflow-hidden rounded-3xl border bg-surface-card p-4 [.booking-flow-card_&]:rounded-18 [.booking-flow-card_&]:shadow-none shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)] ${
              isSelected
                ? "border-primary ring-2 ring-primary"
                : disabled
                  ? "border-border opacity-60"
                  : "border-border"
            }`
      }
      data-testid="category-card"
      aria-disabled={disabled || undefined}
    >
      {includesBeds && <BedsIncludedDescription id={bedsDescriptionId} />}
      {compactUnitStyle ? (
        <>
          {/* Unit view: first image only, no carousel/card wrapper */}
          <div className="relative aspect-[430/300] w-full overflow-hidden rounded-12 xl:aspect-auto xl:h-[140px]">
            {firstImage ? (
              <StableImage
                src={firstImage.url}
                alt={firstImage.description ?? ""}
                className="h-full w-full object-cover"
                onSourceError={handleImageError}
              />
            ) : null}
            <LimitedAvailabilityBadge show={limitedAvailability} />
            <ExtraBedsBadge count={extraBedsCount} pricePerNight={extraBedPricePerNight} />
          </div>

          <div className="flex flex-1 flex-col gap-1">
            {/* Room info rides the title as an icon rather than a full-width
                button above it: it is a way to look closer, not a decision, and
                a button that wide read as the card's primary action. */}
            <div className="flex items-start gap-1">
              <h3 className="min-w-0 text-[18px] font-bold leading-[1.21] text-ink">
                {title}
              </h3>
              {onViewRoomInfo && (
                <button
                  type="button"
                  onClick={onViewRoomInfo}
                  aria-label={t("landing.large_card.view_room_info")}
                  data-testid="category-card-room-info"
                  className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-ink-secondary transition-colors hover:bg-ink/5"
                >
                  <FiInfo className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {showPriceAndSelect && (
              <CardPrice
                price={price}
                originalPrice={originalPrice}
                priceFrom={priceFrom}
                nights={nights}
              />
            )}
            {bedsSlot}
            {areaText && (
              <div className="mt-1 flex items-center gap-1.5">
                <FiLayers className="h-4 w-4 text-ink" />
                <span className="text-xs leading-[1.21] text-ink">
                  {areaText}
                </span>
              </div>
            )}
          </div>

          {showPriceAndSelect && (
            <Button
              variant={isSelected ? "highlighted" : "default"}
              size="small"
              className="mt-auto !h-8 w-full !rounded-full !text-xs"
              type="button"
              disabled={disabled || isSelected}
              onClick={isSelected ? undefined : onSelect}
              onMouseEnter={() => import("../../booking/Booking")}
              data-testid="select-button"
              {...bedsA11y}
            >
              {includesBeds && !isSelected && <BedsIncludedIcon />}
              {t(isSelected ? "landing.large_card.selected" : "landing.large_card.select")}
            </Button>
          )}
        </>
      ) : (
        <>
          {/* Main Image Container */}
          {displayedCurrentImageUrl ? (
            <div
              className="group relative h-[246px] shrink-0 overflow-hidden rounded-lg [.booking-flow-card_&]:rounded-10 md:h-[300px]"
              onClick={handleImageClick}
              style={{ cursor: "zoom-in" }}
            >
              <LimitedAvailabilityBadge show={limitedAvailability} />
              <ExtraBedsBadge count={extraBedsCount} pricePerNight={extraBedPricePerNight} />
              <div className="relative h-full w-full">
                <AnimatePresence mode="sync" initial={false} custom={direction}>
                  <motion.img
                    key={displayedCurrentImageUrl}
                    src={displayedCurrentImageUrl}
                    alt={displayedCurrentImage?.description ?? ""}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={() => handleImageError(displayedCurrentImageUrl)}
                    custom={direction}
                    initial={{ opacity: 0, x: direction > 0 ? 50 : -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: direction > 0 ? -50 : 50 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  />
                </AnimatePresence>

                {images.length > 1 && (
                  <>
                    {/* Prev button */}
                    <button
                      onClick={handlePrevImage}
                      className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-surface-card/80 p-2 opacity-0 transition-all duration-200 hover:scale-110 hover:bg-surface-card group-hover:opacity-100"
                    >
                      <FiChevronRight className="h-5 w-5 rotate-180" />
                    </button>

                    {/* Next button */}
                    <button
                      onClick={handleNextImage}
                      className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-surface-card/80 p-2 opacity-0 transition-all duration-200 hover:scale-110 hover:bg-surface-card group-hover:opacity-100"
                    >
                      <FiChevronRight className="h-5 w-5" />
                    </button>

                    {/* Dots */}
                    <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-1">
                      {images.map((_, index) => (
                        <button
                          key={index}
                          onClick={(e) => handleDotClick(index, e)}
                          className={`h-1.5 w-1.5 rounded-circle transition-all duration-300 ${
                            index === currentImageIndex
                              ? "scale-110 bg-white"
                              : "bg-white/50 hover:bg-white/70"
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {renderDisabledBadge("overlay")}
            </div>
          ) : null}

          {/* Modal for image gallery */}
          <ImageGalleryModal
            images={images}
            isOpen={isGalleryOpen}
            onClose={() => setIsGalleryOpen(false)}
          />

          {/* Title and Price Section */}
          <div className="flex shrink-0 items-start justify-between">
            <div className="flex min-w-0 flex-1 flex-col gap-1 pr-3">
              <Tooltip label={title} disabled={!isTitleTruncated}>
                <h3
                  ref={titleRef}
                  className="line-clamp-2 text-[18px] font-bold leading-[1.21] text-ink [overflow-wrap:anywhere]"
                >
                  {title}
                </h3>
              </Tooltip>
              {showPriceAndSelect && (
                <CardPrice
                  price={price}
                  originalPrice={originalPrice}
                  priceFrom={priceFrom}
                  nights={nights}
                />
              )}
              {!displayedCurrentImageUrl && renderDisabledBadge("inline")}
            </div>
          </div>
          {bedsSlot}

          {/* Content Container */}
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
            {areaText && (
              <div className="flex items-center gap-1.5">
                <FiLayers className="h-3.5 w-3.5 text-ink" />
                <span className="text-xs leading-[1.21] text-ink">
                  {areaText}
                </span>
              </div>
            )}
            {description && (
              <p className="line-clamp-3 break-words text-xs leading-[1.3] text-ink-secondary">
                {description}
              </p>
            )}
          </div>

          <div className="mt-auto pt-1">
            <AmenityChips
              amenities={sortedAmenities}
              wrap
              trailingAction={
                !onViewDetail && (
                  <div className="ml-auto hidden shrink-0 pl-1 lg:flex">
                    {renderActionButton("inline")}
                  </div>
                )
              }
            />
          </div>
          {onViewDetail && (
            <div className="hidden justify-end pt-1 lg:flex">
              {renderActionButton("inline")}
            </div>
          )}
          {renderActionButton("bottom")}
        </>
      )}
    </div>
  );
};
